import React, { useState, useEffect } from 'react';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../../utils/api';
import { RootState } from '../../../store/store';
import SaltedPatientWorkoutDetailComponent from './SaltedPatientWorkoutDetailCompnent'; 
import '../PatientPage.css';
import DocumentViewer from '../../Common/PdfShowComponent';
import { set } from 'react-hook-form';
import { GenderCode, genderMap } from 'store/types/GenderCode';
import { Patient } from '@types';

interface PatientDetail {
  ptCd: string;
  gender: GenderCode;
}

interface WorkoutRecord {
  id: number;
  workoutUuid: string;
  createdAt: string;
}

interface SelectedRecord {
  ptCd: string;
  id: number;
  workoutUuid: string;
  createdAt: string;
}

const SaltedPatientDetailComponent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const user = useSelector((state: RootState) => state.user.user);

  const [patient, setPatient] = useState<PatientDetail | null>(null);
  const [workoutRecords, setWorkoutRecords] = useState<WorkoutRecord[]>([]);
  const [page, setPage] = useState({ skip: 0, take: 10 });

  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [showFileViewer, setShowFileViewer] = useState(false);

  const [selectedRecord, setSelectedRecord] = useState<SelectedRecord | null>(null);
  const [showRecordDetail, setShowRecordDetail] = useState(false);

  useEffect(() => {
    const fetchPatientDetail = async () => {
      if (user?.hospital?.hspCd && id) {
        try {
          const response = await api.getSaltedPatientDetailAsync(id, user.hospital.hspCd);
          console.log('환자 상세 정보:', response.data);
          setPatient(response.data);
          // setPatient(
          //   response.data.map((patient: Patient) => ({
          //     ...patient,
          //     gender: genderMap[patient.gender] || patient.gender
          //   })))
          setWorkoutRecords(response.data.patientWorkoutInfo);
        } catch (error) {
          console.error('환자 상세 정보를 가져오는 중 오류 발생:', error);
        }
      }
    };

    fetchPatientDetail();
  }, [id, user]);

  const onPageChange = (e: any) => setPage(e.page);

  const handleFileClick = async (fileName: string | null) => {
    console.log('파일 클릭:', fileName);
    if (!fileName) return;

    const ext = fileName.split('.').pop()?.toLowerCase();

    if (user?.hospital?.hspCd && ext === 'csv') {
      try {
        const response = await api.getCsvDownloadUrl(user.hospital.hspCd, fileName);
        const presignedUrl = response.data;
        window.open(presignedUrl, '_blank');
      } catch (error) {
        console.error('CSV 다운로드 URL 요청 실패:', error);
      }
      return;
    }

    // PDF 보기
    setSelectedFileName(fileName);
    setShowFileViewer(true);

    // 상세 보기 닫기
    setSelectedRecord(null);
    setShowRecordDetail(false);
  };

  const handleRecordDetail = (record: WorkoutRecord) => {
    if (!patient?.ptCd) return;

    setSelectedRecord({
      ptCd: patient.ptCd,
      id: record.id,
      workoutUuid: record.workoutUuid,
      createdAt: record.createdAt,
    });

    setShowRecordDetail(true);

    // PDF 뷰어 닫기
    setSelectedFileName(null);
    setShowFileViewer(false);
  };

  const closeFileViewer = () => {
    setSelectedFileName(null);
    setShowFileViewer(false);
  };

  const closeRecordDetail = () => {
    setSelectedRecord(null);
    setShowRecordDetail(false);
  };


  const GenderCell = (props: any) => {
    const gender = props.dataItem.gender;
    return <td>{genderMap[gender] || gender}</td>;
  };


  return (
    <div className="layoutContainer PatientDetailPage">
      <h2>환자 상세 정보</h2>
      {patient && (
        <div className="PatientDetail">
          <div className="PatientInfoGrid">
            <h3>기본 정보</h3>
            <Grid data={[patient]}>
              <Column field="name" title="이름" width="150px" />
              <Column field="gender" title="성별" width="80px" cell={GenderCell} />
              <Column field="birthDate" title="생년월일" width="120px" />
              <Column field="phoneNumber" title="전화번호" width="150px" />
            </Grid>
          </div>

          <div className="PatientHealthGrid">
            <h3>운동 기록</h3>
            <div className={`healthViewer ${showFileViewer || showRecordDetail ? 'open' : 'close'}`}>
              <Grid
                className="health"
                data={workoutRecords.slice(page.skip, page.skip + page.take)}
                pageable
                skip={page.skip}
                take={page.take}
                total={workoutRecords.length}
                onPageChange={onPageChange}
              >
                <Column field="createdAt" title="날짜" />
                <Column field="workoutCode" title="운동 코드" />
                <Column
                  field="recordDetail"
                  title="기록 상세"
                  cell={(props) => (
                    <td>
                      <button
                        className="MainBtncolor openBtn"
                        onClick={() => handleRecordDetail(props.dataItem)}
                      >
                        상세 보기
                      </button>
                    </td>
                  )}
                />
                <Column
                  field="fileName"
                  title="결과보고서"
                  cell={(props) => {
                    const fileName = props.dataItem.fileName;
                    if (!fileName) return <td>없음</td>;

                    const ext = fileName.split('.').pop()?.toLowerCase();
                    const buttonLabel = ext === 'pdf' ? '열기' : '다운로드';

                    return (
                      <td>
                        <button
                          className="MainBtncolor openBtn"
                          onClick={() => handleFileClick(fileName)}
                        >
                          {buttonLabel}
                        </button>
                      </td>
                    );
                  }}
                />
              </Grid>

              {selectedRecord && (
                <div className="record-detail-modal">
                  <button className="MainBtncolor closebutton" onClick={closeRecordDetail}>
                    닫기
                  </button>
                  <SaltedPatientWorkoutDetailComponent
                    ptCd={selectedRecord.ptCd}
                    id={selectedRecord.id}
                    workoutUuid={selectedRecord.workoutUuid}
                  />
                </div>
              )}

              {showFileViewer && selectedFileName && (
                <div className="pdf-modal-content">
                  <button
                    className="MainBtncolor closebutton"
                    onClick={closeFileViewer}
                    style={{ float: 'right' }}
                  >
                    닫기
                  </button>
                  <DocumentViewer fileName={selectedFileName} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaltedPatientDetailComponent;
